import React from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Scan, BarChart3 } from "lucide-react";
import { mediaUrl, PLACEHOLDER_COVER } from "@/lib/media";
import { ArrowLink, Progress } from "@/portal/ui/Layout";
import { StatusBadge, PROJECT_STATUS } from "@/portal/ui/Badge";
import { fmtDateLong, untilLabel } from "@/portal/format";

function FactBig({ icon: Icon, label, value, sub, className = "" }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <div className="flex items-center gap-1.5 cp-meta"><Icon className="h-3.5 w-3.5 text-cp-text-3 shrink-0" aria-hidden="true" /><span className="truncate">{label}</span></div>
      <div className="text-[13.5px] font-semibold text-cp-text leading-snug mt-1">{value}</div>
      {sub && <div className="cp-meta mt-0.5">{sub}</div>}
    </div>
  );
}

/** Cardul mare al proiectului principal (imagine + fapte cheie + progres). */
export function FeaturedProject({ p }) {
  const tone = p.client_status === "Delayed" ? "danger" : p.client_status === "Attention Required" ? "warning" : undefined;
  const end = p.estimated_completion || p.end_date;
  return (
    <article className="cp-panel overflow-hidden grid grid-cols-1 md:grid-cols-[minmax(0,40%)_1fr] min-w-0" data-testid={`home-project-${p.code}`}>
      <Link to={`/portal/proiecte/${p.id}`} className="group relative block aspect-[16/10] md:aspect-auto md:h-full md:min-h-[240px] m-3 mb-0 md:mb-3 rounded-[8px] overflow-hidden bg-cp-bg-subtle" data-testid="featured-cover">
        <img src={mediaUrl(p.cover_image) || PLACEHOLDER_COVER} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E2A3F]/80 via-[#0E2A3F]/15 to-transparent" aria-hidden="true" />
        <span className="absolute top-3 left-3"><StatusBadge map={PROJECT_STATUS} value={p.client_status} size="sm" className="!bg-white/95" /></span>
        <p className="absolute left-4 right-4 bottom-4 cp-serif text-white text-[19px] leading-snug drop-shadow-sm" data-testid="featured-overlay">Construim<br />un viitor mai bun, împreună.</p>
      </Link>
      <div className="p-5 md:pl-4 flex flex-col min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="cp-h2 !text-[23px] leading-tight"><Link to={`/portal/proiecte/${p.id}`} className="hover:text-cp-accent transition-colors" data-testid="featured-title">{p.name}</Link></h2>
            <div className="text-[13px] text-cp-text-2 mt-1 flex items-center gap-2 flex-wrap">
              <span className="cp-mono uppercase">{p.code}</span>
              {p.sector && <><span className="text-cp-text-3" aria-hidden="true">|</span><span>{p.sector}</span></>}
            </div>
          </div>
          <ArrowLink to={`/portal/proiecte/${p.id}`} testid={`open-project-${p.code}`} className="shrink-0 mt-1.5">Vezi detalii</ArrowLink>
        </div>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-[1.05fr_0.8fr_1.15fr] gap-4 sm:gap-3">
          <FactBig icon={CalendarDays} label="Termen de finalizare" value={fmtDateLong(end)} sub={untilLabel(end)} />
          <FactBig icon={Scan} label="Suprafață" value={p.surface || "—"} className="sm:pl-3 sm:border-l sm:border-cp-border" />
          <FactBig icon={BarChart3} label="Stadiu actual" value={p.current_stage || "—"} className="sm:pl-3 sm:border-l sm:border-cp-border" />
        </div>
        <div className="mt-auto pt-5">
          <div className="cp-meta mb-1.5">Progres fizic</div>
          <div className="flex items-center gap-3">
            <Progress value={p.progress} tone={tone} className="flex-1 !h-[7px]" label={`Progres ${p.name}`} />
            <span className="text-[14px] font-semibold tabular-nums text-cp-text" data-testid="featured-progress">{p.progress}%</span>
          </div>
        </div>
      </div>
    </article>
  );
}
